/**
 * Het tabblad Resultaten: per leerling per les de status en de score op de
 * meerkeuzevragen ("4/5": vier goed van de vijf in zijn toets), voor alle
 * groepen of voor één. Oefende hij eerst de woorden (stap 29), dan ook
 * hoeveel woorden hij de eerste keer kende, en welke niet; wat hij daarna
 * goed deed, is oefenen en telt niet mee. Apart voor gezegd en gekozen
 * (stap 29e): een woord zeggen is iets anders dan het kiezen uit vier plaatjes
 * (dan raad je één op vier goed), dus die tellen niet samen. "Niet verstaan": in de actieve stand
 * kon de spraakherkenning hem twee keer niet verstaan; gebeurt dat vaak, zet
 * hem dan in Groepen op passief. Een resultaat verwijderen
 * haalt de leerling met zijn antwoorden uit die les.
 */
import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { perGroep } from '../perGroep';

const ROUTE = { spreken: 'gezegd', kiezen: 'gekozen' };

/**
 * De woordenoefening: per route "gezegd 5/8", "gekozen 2/3", met welke woorden hij
 * de eerste keer niet kende, en welke niet verstaan werden.
 */
function Woorden({ w }) {
  if (!w) return '-';
  const routes = Object.entries(w.routes || {});
  return (
    <div data-testid="woorden-resultaat" title={w.standen.length ? `stand: ${w.standen.join(', ')}` : ''}>
      {routes.length ? routes.map(([route, r]) => (
        <div key={route} data-testid={`woorden-${route}`}>
          {ROUTE[route] || route} {r.gekend}/{r.gemeten}
          {r.niet_gekend.length > 0 && <div className="text-small text-muted">niet: {r.niet_gekend.join(', ')}</div>}
        </div>
      )) : (
        <>
          <span>{w.gekend}/{w.gemeten}</span>
          {w.niet_gekend.length > 0 && <div className="text-small text-muted">niet: {w.niet_gekend.join(', ')}</div>}
        </>
      )}
      {w.niet_verstaan.length > 0 && <div className="text-small" style={{ color: 'var(--waarschuw-tekst)' }}>niet verstaan: {w.niet_verstaan.join(', ')}</div>}
    </div>
  );
}

export function Resultaten({ actief, meldFout }) {
  const [resultaten, setResultaten] = useState([]);
  const [laden, setLaden] = useState(false);
  const [groep, setGroep] = useState(null);   // null: alle groepen

  useEffect(() => {
    if (!actief) return;
    setLaden(true);
    api.haalResultaten().then(setResultaten).catch(err => meldFout(err.message)).finally(() => setLaden(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  const verwijder = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit resultaat wilt verwijderen?')) return;
    try {
      await api.verwijderResultaat(id);
      setResultaten(await api.haalResultaten());
    } catch (err) {
      meldFout(err.message);
    }
  };

  if (laden) return <p>Laden...</p>;

  const alle = groep === null;

  return (
    <div className="card">
      <h2 className="mb-16">Resultaten</h2>

      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button className={`btn ${alle ? 'btn-primary' : 'btn-secondary'}`} style={{ padding: '6px 12px' }} onClick={() => setGroep(null)}>
          Alle groepen ({resultaten.length})
        </button>
        {perGroep(resultaten).map(g => (
          <button key={g.class_id} className={`btn ${groep === g.class_id ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '6px 12px' }} onClick={() => setGroep(g.class_id)}>
            {g.class_naam} ({g.items.length})
          </button>
        ))}
      </div>

      <table>
        <thead>
          <tr>
            <th>Leerling</th>
            {alle && <th>Groep</th>}
            <th>Tekst</th>
            <th>Status</th>
            <th>MC Score</th>
            <th title="De woordenoefening: hoeveel woorden hij de eerste keer kende">Woorden</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {resultaten.filter(r => alle || r.class_id === groep).map(r => (
            <tr key={r.id}>
              <td>{r.student_naam}</td>
              {alle && <td>{r.class_naam}</td>}
              <td>{r.text_titel}</td>
              <td>
                <span className={`badge ${r.status === 'afgerond' ? 'badge-success' : 'badge-warning'}`}>{r.status}</span>
              </td>
              <td>{r.mc_score !== null ? `${r.mc_score}/${r.mc_total} (${r.mc_percentage}%)` : '-'}</td>
              <td><Woorden w={r.woorden} /></td>
              <td>
                <button className="btn btn-danger" style={{ padding: '4px 8px' }} onClick={() => verwijder(r.id)}>
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
